/**
 * @fileoverview Lector de QR usando html5-qrcode. Se monta en un modal
 * o sección y llama `onCodigoLeido` cuando detecta un código válido.
 *
 * El QR puede contener:
 *   1. Un código directo "IZT-4837"
 *   2. Una URL completa "https://.../?codigo=IZT-4837"
 * Ambos se normalizan en `extraerCodigoDeQR`.
 */

import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { Camera, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { extraerCodigoDeQR } from "../lib/utils-app";

const QR_REGION_ID = "qr-reader-region";

/**
 * Detiene y limpia un scanner de forma defensiva. La librería html5-qrcode
 * lanza SÍNCRONAMENTE "Cannot stop, scanner is not running or paused" si
 * `start()` aún no terminó (escenario común con React StrictMode o cierre
 * inmediato del modal). Por eso usamos try/catch síncrono además del
 * `.catch()` de la promesa.
 *
 * @param {Html5Qrcode | null} scanner
 */
async function detenerScanner(scanner) {
    if (!scanner) return;
    try {
        const estado = scanner.getState();
        if (
            estado === Html5QrcodeScannerState.SCANNING ||
            estado === Html5QrcodeScannerState.PAUSED
        ) {
            await scanner.stop();
        }
    } catch {
        // intencional: html5-qrcode lanza síncronamente "Cannot stop, scanner
        // is not running or paused" si start() aún no terminó. Es el caso
        // común con React StrictMode o cierre inmediato del modal; lo silenciamos
        // porque el comentario de arriba ya explica el contrato esperado.
    }
    try {
        scanner.clear();
    } catch {
        // intencional: clear() también puede lanzar tras un stop() abortado.
        // No hay nada útil que reportar aquí; el componente se está desmontando.
    }
}

/**
 * @param {{ onCodigoLeido: (codigo: string) => void, onCerrar: () => void }} props
 */
export function QRScanner({ onCodigoLeido, onCerrar }) {
    const [error, setError] = useState(/** @type {string | null} */ (null));
    const [iniciado, setIniciado] = useState(false);
    /** @type {React.MutableRefObject<Html5Qrcode | null>} */
    const scannerRef = useRef(null);

    useEffect(() => {
        let cancelado = false;

        async function iniciar() {
            try {
                const scanner = new Html5Qrcode(QR_REGION_ID);
                scannerRef.current = scanner;
                await scanner.start(
                    { facingMode: "environment" },
                    { fps: 10, qrbox: 240 },
                    (decoded) => {
                        const codigo = extraerCodigoDeQR(decoded);
                        if (codigo) {
                            onCodigoLeido(codigo);
                        }
                    },
                    () => {
                        /* errores por frame: ignorar */
                    }
                );
                if (cancelado) {
                    // El componente se desmontó mientras start() estaba en vuelo;
                    // detenemos el scanner que acabamos de prender.
                    detenerScanner(scanner);
                    return;
                }
                setIniciado(true);
            } catch (e) {
                if (!cancelado) {
                    setError(
                        e instanceof Error
                            ? "No pudimos abrir la cámara. Verifica permisos."
                            : "No pudimos abrir la cámara."
                    );
                }
            }
        }

        iniciar();

        return () => {
            cancelado = true;
            const s = scannerRef.current;
            scannerRef.current = null;
            detenerScanner(s);
        };
    }, [onCodigoLeido]);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Lector de código QR"
            className="fixed inset-0 z-50 bg-foreground/80 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="qr-scanner"
        >
            <div className="bg-card rounded-2xl shadow-xl w-full max-w-md p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="font-serif-headline text-xl font-semibold flex items-center gap-2">
                        <Camera size={20} className="text-primary" />
                        Escanea tu código
                    </h2>
                    <button
                        type="button"
                        onClick={onCerrar}
                        className="p-2 rounded-full hover:bg-secondary"
                        aria-label="Cerrar lector QR"
                        data-testid="qr-close-btn"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div
                    id={QR_REGION_ID}
                    className="aspect-square w-full bg-secondary rounded-xl overflow-hidden"
                />

                {error ? (
                    <p
                        role="alert"
                        className="text-sm text-destructive bg-destructive/10 rounded-lg p-3"
                        data-testid="qr-error"
                    >
                        {error} Si no puedes activar la cámara, escribe tu código
                        manualmente.
                    </p>
                ) : (
                    <p className="text-sm text-muted-foreground">
                        Apunta la cámara al QR de tu papel. {iniciado ? "" : "Iniciando..."}
                    </p>
                )}
            </div>
        </div>
    );
}
